import * as React from 'react'
import { useEditor, EditorContent, type Editor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { cn } from '@/lib/utils'
import { Toolbar } from './Toolbar'
import type { RichTextEditorProps } from './types'
import styles from './styles.module.css'

export const RichTextEditor = React.forwardRef<
  HTMLDivElement,
  RichTextEditorProps
>(
  (
    {
      value,
      onChange,
      placeholder,
      error,
      disabled,
      className,
      id,
      name,
      onBlur,
    },
    ref
  ) => {
    const editorConfig = React.useMemo(() => {
      const attributes: Record<string, string> = {
        class: cn(styles.content, 'prose prose-sm max-w-none'),
      }

      if (id) attributes.id = id
      if (name) attributes.name = name
      if (placeholder) attributes['data-placeholder'] = placeholder

      return {
        extensions: [StarterKit],
        content: value || '',
        editable: !disabled,
        onUpdate: ({ editor }: { editor: Editor }) => {
          onChange(editor.getHTML())
        },
        onBlur,
        editorProps: {
          attributes,
        },
      }
    }, [id, name, placeholder, value, disabled, onChange, onBlur])

    const editor = useEditor(editorConfig)

    React.useEffect(() => {
      if (editor && editor.getHTML() !== value) {
        editor.commands.setContent(value || '')
      }
    }, [value, editor])

    return (
      <div
        ref={ref}
        className={cn(
          styles.wrapper,
          error && styles.error,
          disabled && styles.disabled,
          'focus-within:ring-1 focus-within:ring-ring',
          className
        )}
      >
        <Toolbar editor={editor} disabled={disabled} />
        <EditorContent editor={editor} />
      </div>
    )
  }
)

RichTextEditor.displayName = 'RichTextEditor'
